import * as React from 'react';

import { Cell, flexRender, type Row } from '@tanstack/react-table';

import { TableCell, TableRow } from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from '@/lib/utils';

import type { TableVariants } from './types';
import { buildRowMouseHandlers } from './utils/row-navigation';

const MOUNT_FADE_MS = 200;

interface VirtualDataRowProps<TData> {
  row: Row<TData>;
  rowIndex: number;
  translateY: number;
  isScrolled: boolean;
  variant?: TableVariants;
  rowClassName?: string;
  onRowClick?: (row: TData) => void;
  /** Detail-route href for a row — enables CMD/CTRL+click and middle-click "open in new tab". */
  getRowHref?: (row: TData) => string | undefined;
  getCellWidth: (cell: Cell<TData, unknown>) => number;
  /**
   * Signature of the current visible-column arrangement (ordered visible-leaf ids). Not read
   * by the component — it exists purely so `React.memo` re-renders the row when columns are
   * reordered/hidden. Without it, the memoized row (TanStack reuses `row` identity) would keep
   * its stale cell order while the header updated. See `virtualized-table-body`.
   */
  columnArrangementKey: string;
  /** Composed ref: virtualizer.measureElement + the nested-pagination observer. */
  rowRef: (el: HTMLTableRowElement | null) => void;
  /**
   * Explicit row width (the full table width, px). `width: 100%` doesn't reliably
   * resolve against the block-level tbody, and left-pinned sticky cells can only
   * slide within the row box — so the row must span the whole scrollable width.
   */
  rowWidth?: number;
}

/** Matches the expand animation pace of the week-view expandable rows. */
const ROW_SHIFT_ANIMATION_MS = 200;

const baseRowStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  display: 'flex',
  // Rows below an expanding/collapsing section glide to their new offsets instead of
  // snapping (scrolling never animates: an item's content offset is scroll-independent).
  // ONLY `top`/`opacity` are transitioned here — never `transform`: a transform makes
  // the row a containing block for its descendants and breaks `position: sticky` on
  // left-pinned cells (this is also why we don't use the `animate-in`/`fade-in`
  // Tailwind utilities for the mount fade below — their shared `enter` keyframe always
  // sets a `transform`, even with every enter-* variable at its identity default).
  transition: `top ${ROW_SHIFT_ANIMATION_MS}ms ease-out, opacity ${MOUNT_FADE_MS}ms ease-out`,
};

/**
 * One virtualized data row. Renders its cells directly (no deferred/skeleton phase): the cells
 * are memoized and cheap, so a synchronous render keeps vertical scrolling smooth. Any brief
 * "not yet mounted" gap during a very fast scroll shows the table's white backstop (set on the
 * scroll container), not a grey glitch.
 */
function VirtualDataRowComponent<TData>({
  row,
  rowIndex,
  translateY,
  isScrolled,
  variant,
  rowClassName,
  onRowClick,
  getRowHref,
  getCellWidth,
  rowRef,
  rowWidth,
}: VirtualDataRowProps<TData>) {
  // Mount fade (opacity only — see the `transition` comment above for why this can't be
  // the `animate-in`/`fade-in` utilities). Starts at 0 and is flipped to 1 one frame after
  // mount so the browser has a committed starting value to transition from; rows revealed
  // by scrolling are within the overscanned/off-viewport range so the fade is invisible
  // there, and only really shows for rows freshly added by expanding a project/member.
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => setHasMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <TableRow
      ref={rowRef}
      data-index={rowIndex}
      data-state={row.getIsSelected() && 'selected'}
      // `border-0`: the divider is drawn on the cells (see below). An absolutely-positioned
      // row's own bottom border doubles up with the cell border on the left edge.
      className={cn(
        'border-0',
        (onRowClick || getRowHref) && 'group cursor-pointer hover:bg-muted/50',
        rowClassName
      )}
      // Positioned with `top` (NOT `transform: translateY`): a transform makes the row the
      // containing block for its descendants and breaks `position: sticky` on left-pinned
      // cells. `top` keeps the row un-transformed so pinned cells stick to the horizontal
      // scroll container exactly like in the non-virtual path.
      style={{
        ...baseRowStyle,
        top: translateY,
        width: rowWidth ?? '100%',
        opacity: hasMounted ? 1 : 0,
      }}
    >
      {row.getVisibleCells().map(cell => {
        const cellClassName = cell.column.columnDef.meta?.cellClassName;
        const width = getCellWidth(cell);
        const isPinned = cell.column.getIsPinned();
        const pinningStyles = getCommonPinningStyles({
          column: cell.column,
          withBorder: isScrolled,
        });

        return (
          <TableCell
            key={cell.id}
            variant={variant}
            style={{
              ...pinningStyles,
              display: 'flex',
              alignItems: 'center',
              width,
              flex: `0 0 ${width}px`,
            }}
            // Divider on the cell (not the absolute <tr>): an absolutely-positioned row's
            // bottom border gets hidden by sub-pixel overlap with the next row's top, so we
            // draw the line on each contiguous cell instead. Pinned cells keep the same
            // OPAQUE backgrounds as the default cell variant (`bg-background`, hover
            // `bg-surface`): a translucent tint here lets horizontally scrolled content
            // bleed through beneath the sticky column, and a selected-only tint on just
            // the pinned cells reads as a stuck hover highlight (the rest of the row's
            // cells paint opaque backgrounds over the row-level selected tint).
            className={cn(
              'border-b border-border',
              isPinned && 'bg-background group-hover:bg-surface',
              cellClassName,
              rowClassName
            )}
            {...(cell.column.id !== 'select'
              ? buildRowMouseHandlers({ row: row.original, onRowClick, getRowHref })
              : {})}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export const VirtualDataRow = React.memo(VirtualDataRowComponent) as typeof VirtualDataRowComponent;
